/**
 * The single source of truth for "who is signed in". Bootstraps once from
 * `GET /v1/auth/session` (cookie-based; a 401 simply means signed out), listens
 * for the client's 401 broadcast so an expired session signs the app out
 * everywhere at once, and owns sign-out.
 */
import React, { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { isApiError, retryGaps, UNAUTHENTICATED_EVENT } from '../api/client';
import { qk } from '../api/hooks';
import type { MeResponse } from '../api/types';
import { identify, track } from '../lib/analytics';

// `MeResponse` carries no "you just signed up" flag — this is auth via
// magic link only, so there's no separate signup/login request to hook
// either. A signup is a sign-in landing within seconds of account creation;
// generous enough for the verify redirect's own latency, tight enough that
// a real returning login (whose account is always older than this) can't
// be misread as one.
const SIGNUP_WINDOW_MS = 10_000;

export type AuthStatus = 'loading' | 'signed-out' | 'signed-in' | 'error';

interface AuthContextValue {
  status: AuthStatus;
  user: MeResponse | null;
  /** Re-read the session (after a magic-link landing, an email change, an onboarding step…). */
  refresh: () => Promise<MeResponse | null>;
  /** Merge a fresh MeResponse (e.g. the result of PATCH /v1/me) without a round trip. */
  setUser: (u: MeResponse) => void;
  /**
   * End the session on the server, then locally. `thenNavigate` runs in the same transition as the
   * local end, so a caller leaving `/app` lands where it meant to, not on the auth guard's `/auth?next=`.
   */
  signOut: (thenNavigate?: () => void) => Promise<void>;
  /** Forget the session locally when the server has already ended it (e.g. `DELETE /v1/me`). */
  endSession: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUserState] = useState<MeResponse | null>(null);

  // Concurrent callers share one request. StrictMode double-invokes the
  // bootstrap effect in dev, and a remount could do the same in production;
  // either way the session is read once.
  const inFlight = useRef<Promise<MeResponse | null> | null>(null);
  // `refresh` runs on every profile change too (see this file's own
  // top comment), not just on landing signed-in for the first time —
  // without this, a PATCH /v1/me would look like a fresh sign-in.
  const wasSignedIn = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return inFlight.current;
    const p = (async () => {
    try {
      const res = await retryGaps(() => authApi.session());
      setUserState(res.user);
      qc.setQueryData(qk.me, res.user);
      setStatus('signed-in');
      identify(res.user.id);
      if (!wasSignedIn.current) {
        wasSignedIn.current = true;
        const justSignedUp = Date.now() - new Date(res.user.memberSince).getTime() < SIGNUP_WINDOW_MS;
        track(justSignedUp ? 'account_signed_up' : 'account_logged_in');
      }
      return res.user;
    } catch (e) {
      if (isApiError(e) && e.status === 401) {
        setUserState(null);
        setStatus('signed-out');
        return null;
      }
      // Network / 5xx: don't bounce the user to sign-in for an outage.
      setStatus((s) => (s === 'signed-in' ? s : 'error'));
      return null;
    }
    })();
    inFlight.current = p;
    try { return await p; } finally { inFlight.current = null; }
  }, [qc]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const endSession = useCallback(() => {
    setUserState(null);
    setStatus('signed-out');
    wasSignedIn.current = false;
    qc.clear();
  }, [qc]);

  useEffect(() => {
    window.addEventListener(UNAUTHENTICATED_EVENT, endSession);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, endSession);
  }, [endSession]);

  // Keep the context in step with the `me` query, which PATCH /v1/me updates.
  useEffect(() => {
    return qc.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.query.queryKey[0] === 'me' && event.query.queryKey.length === 1) {
        const me = event.query.state.data as MeResponse | undefined;
        if (me) setUserState(me);
      }
    });
  }, [qc]);

  const setUser = useCallback((u: MeResponse) => {
    setUserState(u);
    qc.setQueryData(qk.me, u);
  }, [qc]);

  // Clearing the auth state is an urgent update, while React Router navigates in a transition. Done
  // apart, `RequireAuth` re-renders the old /app route signed-out first and redirects to /auth?next=
  // (ticket 58; ticket 52 hit the same race with account deletion). One transition avoids that.
  const signOut = useCallback(async (thenNavigate?: () => void) => {
    try {
      await authApi.signOut();
    } finally {
      startTransition(() => { thenNavigate?.(); endSession(); });
    }
  }, [endSession]);

  const value = useMemo(() => ({ status, user, refresh, setUser, signOut, endSession }), [status, user, refresh, setUser, signOut, endSession]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
